import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * **점령전별 공성률 선그래프.**
 *
 * 점 하나가 **점령전 한 판**이다. 총 공성률 하나로는 "요즘도 잘 뚫나" 를 알 수 없다 —
 * 잘하던 때와 못하던 때가 그 숫자 안에 묻힌다. 날짜로 묶지 않고 판(그룹)으로 묶는 이유는
 * 점령전이 이틀에 걸쳐 벌어져서다(날짜로 묶으면 한 판이 두 점으로 쪼개진다). 묶는 일은
 * 서버가 한다(`/api/posts/siege-attack-rate`).
 *
 * **눈금은 0~100% 를 다 그린다**(사용자 지시). 공성률이 대체로 85~96% 라 선은 위쪽에
 * 붙어 흐르지만, 눈금을 자르면 작은 차이가 크게 보여 그림이 실제보다 요동쳐 보인다.
 *
 * **한 화면에 다 넣는다.** 옆으로 미는 막대가 생기면 뒤쪽 판이 가려져 흐름이 안 보인다.
 * 그래서 칸 너비를 재서 점 간격을 나눠 쓴다. 대신 간격이 좁아지면 날짜와 숫자가 겹치므로,
 * 글자가 들어갈 만큼만 **건너뛰며** 적는다(양 끝은 항상 적는다).
 *
 * **그래프 밑 스크롤바로 기간을 고른다** (사용자 2026-10-27 — 초안 여럿 끝에 "오른쪽에서 왼쪽으로"). 오른쪽 끝은 늘 '지금',
 * 손잡이 왼쪽 끝을 왼쪽으로 끌면 더 예전 판까지 들어간다. 그래프 점을 눌러도 그 판부터. 고른 판은 그래프에 띠로 칠하고 밖은 흐리게.
 * 고른 기간은 `onPeriodChange(첫 판 시작일, 판 수)` 로 알린다 — 아래 목록이 그 기간으로 다시 센다(서버 `since`).
 * 끄는 동안은 그림만 바꾸고 손을 뗄 때 한 번 알린다(끄는 동안 목록을 계속 다시 부르지 않게).
 */

interface Siege {
    name: string;
    season: number;
    startDate: string;
    wins: number;
    losses: number;
    total: number;
    rate: number;
}

interface Props {
    /** 주면 그 길드원만, 없으면 길드 전체 */
    wizardId?: number | null;
    season?: number | null;
    isMobile: boolean;
    /**
     * 방덱 통계에서 쓸 때 — **방성률**(막아낸 비율)로 그린다. opp = 상대 방덱(우리 공격 기록을 뒤집는다: 우리가 못 뚫은 것 = 막아냄),
     * own = 우리 방덱(우리 방어 기록 — 서버 side=own). 없으면 공성률.
     */
    defense?: 'opp' | 'own';
    /** 기간을 고르면 — since = 고른 첫 판의 시작일(전체면 null), n = 고른 판 수(전체면 0) */
    onPeriodChange?: (since: string | null, n: number) => void;
}

const SiegeRateTrend: React.FC<Props> = ({ wizardId, season, isMobile, defense, onPeriodChange }) => {
    const [sieges, setSieges] = useState<Siege[]>([]);
    const [loading, setLoading] = useState(true);
    /** 고른 판 수 (0 = 전체). 그래프가 새로 오면(시즌·길드원이 바뀌면) 전체로 돌린다 */
    const [pick, setPick] = useState(0);
    const onPeriodRef = useRef(onPeriodChange);
    onPeriodRef.current = onPeriodChange;

    useEffect(() => {
        let alive = true;
        setLoading(true);
        const q = new URLSearchParams();
        if (season) q.set('season', String(season));
        if (wizardId) q.set('wizardId', String(wizardId));
        if (defense === 'own') q.set('side', 'own');
        fetch(`/api/posts/siege-attack-rate?${q.toString()}`)
            .then((r) => r.json())
            .then((d) => {
                if (!alive) return;
                const list: Siege[] = Array.isArray(d?.sieges) ? d.sieges : [];
                // 상대 방덱의 방성률 = 우리 공격이 못 뚫은 비율 — 승/패를 뒤집는다
                setSieges(defense === 'opp'
                    ? list.map((s) => ({ ...s, wins: s.losses, losses: s.wins, rate: parseFloat((100 - s.rate).toFixed(1)) }))
                    : list);
                setPick(0);
                onPeriodRef.current?.(null, 0);
            })
            .catch(() => { if (alive) setSieges([]); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [wizardId, season, defense]);

    /** 눈금 바닥. 0 부터 100 까지 다 그린다 */
    const floor = 0;
    /**
     * 눈금 꼭대기 — 공성률은 100%. **방성률은 가장 높은 판에 맞춘다**(사용자 2026-10-27 "방성률은 100% 기준이 아니라 가장 높은거 기준").
     * 방성률은 대개 5~15% 라 100% 눈금이면 선이 바닥에 붙어 판끼리 차이가 안 보인다. 4로 나눠 떨어지게 4% 단위로 올린다.
     */
    const top = defense ? Math.max(4, Math.ceil(Math.max(0, ...sieges.map((s) => s.rate)) / 4) * 4) : 100;

    const nSel = pick > 0 && pick < sieges.length ? pick : sieges.length;
    const s0 = sieges.length - nSel;          // 고른 첫 판 자리
    const overall = useMemo(() => {
        const sel = sieges.slice(s0);
        const w = sel.reduce((a, s) => a + s.wins, 0);
        const n = sel.reduce((a, s) => a + s.total, 0);
        return n ? parseFloat(((w / n) * 100).toFixed(1)) : 0;
    }, [sieges, s0]);

    /** 판 수를 정해 알린다 — 전체(=그래프 판 수)면 0 */
    const commit = (n: number) => {
        const k = Math.min(sieges.length, Math.max(1, n));
        const all = k >= sieges.length;
        setPick(all ? 0 : k);
        onPeriodRef.current?.(all ? null : sieges[sieges.length - k].startDate, all ? 0 : k);
    };
    const trackRef = useRef<HTMLDivElement>(null);
    const dragging = useRef(false);
    /** 스크롤바 위 손가락 자리 → 판 수 (오른쪽 끝 = 1판) */
    const nAt = (clientX: number) => {
        const g = trackRef.current!.getBoundingClientRect();
        return Math.min(sieges.length, Math.max(1, Math.round(((g.right - clientX) / g.width) * sieges.length)));
    };

    /**
     * 그림이 놓일 칸 너비 — 여기에 맞춰 점 간격을 정한다.
     *
     * **칸이 새로 생길 때마다 다시 잰다**(콜백 ref). 예전에는 판 수가 바뀔 때만 다시 쟀는데, 길드원을 바꾸면
     * 칸이 "불러오는 중" 으로 바뀌었다가 **새 칸**으로 다시 그려진다 — 판 수가 같으면 버려진 옛 칸만 보고 있어서
     * 처음 잰 좁은 너비가 남아 그래프가 오른쪽이 빈 채로 **잘려 보였다**(폰, 2026-10-10).
     */
    const [boxEl, setBoxEl] = useState<HTMLDivElement | null>(null);
    const [boxW, setBoxW] = useState(0);
    useEffect(() => {
        if (!boxEl) return;
        const read = () => setBoxW(boxEl.clientWidth);
        read();
        if (typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(read);
        ro.observe(boxEl);
        return () => ro.disconnect();
    }, [boxEl]);

    const H = isMobile ? 120 : 160;      // 선이 그려지는 높이
    /**
     * 위 여백. 100% 에 가까운 판은 점 위에 적는 숫자가 그림 밖으로 나가 **윗부분이 잘렸다**.
     * 눈금 글자도 같은 만큼 내려 줘야 선과 높이가 맞는다.
     */
    const TOP = 20;
    const PAD = isMobile ? 18 : 26;      // 좌우 여백 (첫 점·끝 점이 잘리지 않게)
    const W = Math.max(200, boxW);
    const STEP = sieges.length > 1 ? (W - PAD * 2) / (sieges.length - 1) : 0;

    /** 글자가 겹치지 않게 몇 칸마다 적을지 */
    const stride = Math.max(1, Math.ceil((isMobile ? 32 : 36) / Math.max(1, STEP)));
    const showAt = (i: number) => i % stride === 0 || i === sieges.length - 1;

    const x = (i: number) => PAD + i * STEP;
    const y = (rate: number) => H - ((rate - floor) / Math.max(1, top - floor)) * H;

    const line = sieges.map((s, i) => `${x(i)},${y(s.rate).toFixed(1)}`).join(' ');
    const area = sieges.length
        ? `M ${x(0)},${H} L ${sieges.map((s, i) => `${x(i)},${y(s.rate).toFixed(1)}`).join(' L ')} L ${x(sieges.length - 1)},${H} Z`
        : '';

    /** 가로 눈금 — 꼭대기를 넷으로 나눈 자리 (공성률이면 0 · 25 · 50 · 75 · 100) */
    const guides = [0, 1, 2, 3, 4].map((k) => (top * k) / 4);

    return (
        <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: 24,
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
            padding: isMobile ? 16 : 24,
            width: '100%',
            boxSizing: 'border-box',
            marginBottom: isMobile ? 16 : 24
        }}>
            {/*
              제목과 전체 값을 **붙여 놓는다.** 양 끝으로 벌려 놓았더니 전체 값이 오른쪽 빈 자리를
              혼자 차지해, 그림과 상관없는 것이 따로 떠 있는 것처럼 보였다.
            */}
            <div style={{
                display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap',
                marginBottom: isMobile ? 12 : 16, textAlign: 'left'
            }}>
                {/* 제목 차림은 화면 제목(공덱 통계 등)과 같은 그라데이션 글씨. 크기만 줄인다 */}
                <div style={{
                    fontSize: isMobile ? 18 : 24, fontWeight: 900, letterSpacing: -0.5,
                    background: 'linear-gradient(135deg, #1976d2 0%, #764ba2 100%)',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                    lineHeight: 1.3
                }}>{defense ? '방성률' : '공성률'}</div>
                {!loading && sieges.length > 0 && (
                    <span style={{ whiteSpace: 'nowrap' }}>
                        <span style={{ fontSize: isMobile ? 15 : 18, fontWeight: 900, color: '#667eea' }}>{overall}%</span>
                        {/* 고른 기간 — 공성률 오른쪽에 (사용자 2026-10-27) */}
                        <span style={{ fontSize: isMobile ? 11 : 12, color: '#aab', fontWeight: 700, marginLeft: 8 }}>
                            {nSel === sieges.length ? '전체' : `최근 ${nSel}회`} · {sieges[s0]?.name} ~ {sieges[sieges.length - 1]?.name}
                        </span>
                    </span>
                )}
            </div>

            {loading ? (
                <div style={{ height: H, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#667eea', fontWeight: 600 }}>
                    불러오는 중...
                </div>
            ) : sieges.length === 0 ? (
                <div style={{ height: H, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                    {defense === 'own' ? '방어' : '공격'} 기록이 없습니다.
                </div>
            ) : (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                    {/* 왼쪽 눈금 — 자른 바닥을 숨기지 않는다. 옆으로 밀어도 남아 있어야 해서 밖에 둔다 */}
                    <div style={{
                        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                        height: H, fontSize: 10, color: '#bbb', fontWeight: 700,
                        flexShrink: 0, textAlign: 'right', marginTop: TOP
                    }}>
                        {[...guides].reverse().map((g) => <span key={g}>{g}%</span>)}
                    </div>

                    <div ref={setBoxEl} style={{ flex: 1, minWidth: 0 }}>
                        <svg width={W} height={H + TOP + 20} style={{ display: 'block' }}>
                            <defs>
                                <linearGradient id="siegeRateLine" x1="0" y1="0" x2="1" y2="0">
                                    <stop offset="0%" stopColor="#667eea" />
                                    <stop offset="100%" stopColor="#764ba2" />
                                </linearGradient>
                                <linearGradient id="siegeRateArea" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#667eea" stopOpacity="0.22" />
                                    <stop offset="100%" stopColor="#667eea" stopOpacity="0" />
                                </linearGradient>
                            </defs>

                            {/* 가로 눈금선 */}
                            <g transform={`translate(0, ${TOP})`}>
                                {guides.map((g) => (
                                    <line
                                        key={g}
                                        x1={0} x2={W} y1={y(g)} y2={y(g)}
                                        stroke="rgba(102, 126, 234, 0.12)" strokeWidth={1}
                                        strokeDasharray={g === top ? undefined : '4 4'}
                                    />
                                ))}

                                {/* 고른 기간 띠 */}
                                {nSel < sieges.length && (
                                    <rect
                                        x={x(s0) - STEP / 2} y={-TOP + 4} width={STEP * nSel} height={H + TOP - 4}
                                        rx={8} fill="rgba(102, 126, 234, 0.10)"
                                    />
                                )}
                                <path d={area} fill="url(#siegeRateArea)" />
                                <polyline
                                    points={line}
                                    fill="none"
                                    stroke="url(#siegeRateLine)"
                                    strokeWidth={2.5}
                                    strokeLinejoin="round"
                                    strokeLinecap="round"
                                />

                                {sieges.map((s, i) => (
                                    <g key={i} onClick={() => commit(sieges.length - i)} style={{ cursor: 'pointer', opacity: i < s0 ? 0.35 : 1 }}>
                                        {/* 누를 자리 — 점만으로는 손가락에 작다 */}
                                        <rect x={x(i) - STEP / 2} y={-TOP} width={Math.max(STEP, 1)} height={H + TOP + 20} fill="transparent" />
                                        <circle cx={x(i)} cy={y(s.rate)} r={4} fill="#fff" stroke="#667eea" strokeWidth={2.5} />
                                        {showAt(i) && (
                                            <text
                                                x={x(i)} y={y(s.rate) - 10}
                                                textAnchor="middle"
                                                fontSize={isMobile ? 9 : 10}
                                                fontWeight={800}
                                                fill="#667eea"
                                            >{Math.round(s.rate)}</text>
                                        )}
                                        {showAt(i) && (
                                            <text
                                                x={x(i)} y={H + 14}
                                                textAnchor="middle"
                                                fontSize={isMobile ? 9 : 10}
                                                fontWeight={600}
                                                fill="#aaa"
                                            >{s.name}</text>
                                        )}
                                        {/* 자세한 값은 점 위에 마우스를 올리면 */}
                                        <title>{`${s.name} · ${s.total}회 중 ${s.wins}${defense ? '막음' : '뚫음'} (${s.rate}%)`}</title>
                                    </g>
                                ))}
                            </g>
                        </svg>
                        {/* 기간 스크롤바 — 오른쪽 끝이 지금, 손잡이 왼쪽 끝을 끌어 늘린다. 점 간격(PAD·STEP)에 맞춰 판과 같은 자리에 둔다 */}
                        <div
                            ref={trackRef}
                            onPointerDown={(e) => { dragging.current = true; e.currentTarget.setPointerCapture(e.pointerId); setPick(nAt(e.clientX)); }}
                            onPointerMove={(e) => { if (dragging.current) setPick(nAt(e.clientX)); }}
                            onPointerUp={(e) => { if (!dragging.current) return; dragging.current = false; commit(nAt(e.clientX)); }}
                            onPointerCancel={() => { dragging.current = false; commit(nSel); }}
                            style={{
                                position: 'relative', height: isMobile ? 14 : 16, borderRadius: 999,
                                margin: `2px ${Math.max(0, PAD - STEP / 2)}px 0`,
                                background: 'rgba(102, 126, 234, 0.12)', cursor: 'pointer', touchAction: 'none'
                            }}
                        >
                            <div style={{
                                position: 'absolute', top: 0, bottom: 0, right: 0, width: `${(nSel / sieges.length) * 100}%`,
                                borderRadius: 999, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                            }}>
                                <div style={{
                                    position: 'absolute', top: -4, bottom: -4, left: -4, width: 12, borderRadius: 6,
                                    background: '#4a4fc0', boxShadow: '0 1px 4px rgba(0,0,0,0.25)', cursor: 'ew-resize'
                                }} />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SiegeRateTrend;
